/**
 * JsonEditor React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// react
import React from 'react';
import PropTypes from 'prop-types';

// material UI
import {withStyles} from "@material-ui/core";

// Material UI components
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';

// 3rd party
import _ from 'lodash';



function stringifyReadable(value) {
    if (value == null) return "";
    else return JSON.stringify(value, null, 4);
}


/**
 * JsonEditor is a React component for editing JSON objects directly
 *
 * @author Jens Gruschel
 */
export class JsonEditor extends React.Component {

    constructor(props) {

        super(props);

        this.state = {
            value: props.value,
            text: stringifyReadable(props.value),
            error: null
        };
    }

    componentDidUpdate(prevProps) {

        if (!_.isEqual(this.props.value, this.state.value)) {

            this.setState({
                value: this.props.value,
                text: stringifyReadable(this.props.value),
                error: null
            });
        }
    }

    handleChange(event) {

        if (event.target.value === "") {
            this.setState({
                value: undefined,
                text: "",
                error: null
            });
            this.props.onChange(undefined);
        }
        else {    
            try {
                const value = JSON.parse(event.target.value);
                this.setState({
                    value: value,
                    text: event.target.value,
                    error: null
                });
                this.props.onChange(value);
            }
            catch (e) {
                this.setState({
                    // keep value
                    text: event.target.value,
                    error: e.message
                });
            }
        }
    };

    render() {

        const { classes, label, readOnly, helperText } = this.props;

        const inputProps = {
            style: {
                fontFamily: "monospace,monospace"
            },
            readOnly: readOnly
        };

        if (this.state.error) {
            inputProps.endAdornment = <InputAdornment position="end" className={classes.errorAdornment}>
                <Typography component="p" variant="caption" color="error" className={classes.errorLabel}>{this.state.error}</Typography>
            </InputAdornment>;
        }

        return <TextField
            label={label}
            value={this.state.text}
            disabled={readOnly} // TODO: make text selectable for copy/paste
            className={classes.codeField}
            InputProps={inputProps}
            onChange={event => this.handleChange(event)}
            margin="normal"
            multiline={true}
            fullWidth={true}
            type="text"
            helperText={helperText}
            variant="outlined"
            rows={15}
            rowsMax={20}
        />;
    }
}

JsonEditor.propTypes = {
    label: PropTypes.string,
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    helperText: PropTypes.string
};

const styles = {
    codeField: {
        marginTop: 16
    },
    errorAdornment: {
        position: "absolute",
        bottom: 0,
        left: 0
    },
    errorLabel: {
        backgroundColor: "#fff",
        paddingLeft: 5,
        paddingRight: 5
    }
};


export default withStyles(styles)(JsonEditor);